/**
 * Opening a shared recipe link, <BASE_URL>/shared#r=<token> (shared/sharing.ts). Before the app
 * renders, the token moves from the address into this tab's sessionStorage and leaves the address
 * bar (history.replaceState): it survives signing in and reloads, and only ever reaches the server
 * in a POST body. Replacing the address cleans this tab's history entry, but the browser's own
 * history may still list the first visit, as it would any link; turning the link off or making a
 * new one is how a link stops working. The token is forgotten once the recipe is added or the
 * preview dismissed, and when a session ends. Where storage is blocked it's kept in memory, for
 * this page only.
 */
import { SHARE_TOKEN_PATTERN, SHARED_RECIPE_PATH } from '../../shared/sharing';

export const SHARED_TOKEN_KEY = 'grams.sharedRecipe';

/** The parts of Web Storage, location and history used here, so tests can pass their own. */
export interface TokenStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}
export interface PageAddress {
  pathname: string;
  hash: string;
}
export interface PageHistory {
  state: unknown;
  replaceState(state: unknown, unused: string, url: string): void;
}

/** The token when storage is blocked (private browsing, say). */
let kept: string | null = null;

function tabStore(): TokenStore | undefined {
  try {
    return (globalThis as { sessionStorage?: TokenStore }).sessionStorage;
  } catch {
    // Reading sessionStorage itself throws where storage is blocked.
    return undefined;
  }
}

/** The token in a link's fragment ("#r=<token>"), or null when it isn't a complete one. */
export function tokenFromHash(hash: string): string | null {
  if (!hash.startsWith('#r=')) return null;
  const token = hash.slice(3);
  return SHARE_TOKEN_PATTERN.test(token) ? token : null;
}

/** Keeps a link's token for this tab until the recipe is added or the preview dismissed. */
export function keepSharedToken(token: string, store: TokenStore | undefined = tabStore()): void {
  kept = token;
  try {
    store?.setItem(SHARED_TOKEN_KEY, token);
  } catch {
    // Full or blocked storage: memory, for this page.
  }
}

/**
 * A link just opened: its token is kept. A broken one is dropped, along with any link this tab had
 * before, so the page never shows an older recipe in its place.
 */
export function takeSharedLink(hash: string, store: TokenStore | undefined = tabStore()): void {
  const token = tokenFromHash(hash);
  if (token) keepSharedToken(token, store);
  else forgetSharedToken(store);
}

/** On load: a shared link's token goes into storage and out of the address bar. Other addresses are left alone. */
export function captureSharedLink(
  address: PageAddress | undefined = (globalThis as { location?: PageAddress }).location,
  history: PageHistory | undefined = (globalThis as { history?: PageHistory }).history,
  store: TokenStore | undefined = tabStore(),
): void {
  if (!address || !history || address.pathname !== SHARED_RECIPE_PATH || !address.hash.startsWith('#r=')) return;
  takeSharedLink(address.hash, store);
  history.replaceState(history.state, '', SHARED_RECIPE_PATH);
}

/** The link this tab is showing, if any. */
export function sharedToken(store: TokenStore | undefined = tabStore()): string | null {
  try {
    const saved = store?.getItem(SHARED_TOKEN_KEY) ?? null;
    if (saved && SHARE_TOKEN_PATTERN.test(saved)) return saved;
  } catch {
    // Blocked storage: whatever's in memory.
  }
  return kept;
}

/** Done with the link: the recipe was added, or the preview dismissed. */
export function forgetSharedToken(store: TokenStore | undefined = tabStore()): void {
  kept = null;
  try {
    store?.removeItem(SHARED_TOKEN_KEY);
  } catch {
    // Blocked storage: nothing was kept there.
  }
}
